import { preloadSdkDnd } from '@Pages/pageRuntime/dnd/lazyDnd';
import { installPageHost } from '@Pages/pageRuntime/modules/pageHost';
import { preloadSdkMembers } from '@Pages/pageRuntime/sdkMemberLoaders';

export type LoadedWorkspaceModule =
  | { Component: React.FunctionComponent<Record<string, unknown>>; error: null }
  | { Component: null; error: Error };

type ModuleImporter = (specifier: string) => Promise<{ default?: unknown }>;

type ShimImporter = (specifier: string) => Promise<{ default?: unknown }>;

let shimImporter: Promise<ShimImporter> | null = null;

function documentImportMap(): Record<string, string> {
  const parsed: unknown = JSON.parse(
    document.querySelector('script[type="importmap"]')?.textContent ?? '{}'
  );
  const imports: unknown =
    typeof parsed === 'object' && parsed !== null ? Reflect.get(parsed, 'imports') : null;

  return Object.fromEntries(
    Object.entries(typeof imports === 'object' && imports !== null ? imports : {}).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );
}

/**
 * Safari before 16.4 and Firefox before 108 cannot read an import map. There,
 * es-module-shims loads each module and the modules it imports through the
 * document's import map. Shim mode leaves the document's other scripts alone.
 */
function loadShimImporter(): Promise<ShimImporter> {
  shimImporter ??= (async () => {
    window.esmsInitOptions = { shimMode: true, noLoadEventRetriggers: true };
    await import('es-module-shims');
    window.importShim.addImportMap({ imports: documentImportMap() });

    return (specifier: string) => window.importShim(specifier);
  })();

  return shimImporter;
}

function supportsImportMaps(): boolean {
  return (
    typeof HTMLScriptElement.supports === 'function' && HTMLScriptElement.supports('importmap')
  );
}

const importByMap: ModuleImporter = async (specifier) =>
  supportsImportMaps()
    ? import(/* @vite-ignore */ specifier)
    : (await loadShimImporter())(specifier);

const loads = new Map<string, Promise<LoadedWorkspaceModule>>();

/**
 * Loads a prebuilt workspace module by its import map specifier and returns
 * its default export. The document's import map resolves the specifier and
 * every module it imports, so navigation loads only the modules that the next
 * page reaches. The lazy Page SDK members that the build recorded load
 * together with the module, so the module's first render has them. A failed
 * load resolves to its error and stays cached, so the renderer that suspended
 * on it shows the error instead of importing again on every render; a
 * document reload tries again.
 */
export function loadWorkspaceModule(
  specifier: string,
  features: readonly string[],
  sdkMembers: readonly string[] | null,
  importer: ModuleImporter = importByMap
): Promise<LoadedWorkspaceModule> {
  const key = `${specifier}\u0000${features.join(',')}\u0000${sdkMembers?.join(',') ?? ''}`;
  let load = loads.get(key);

  if (load === undefined) {
    load = importModule(specifier, features, sdkMembers, importer);
    loads.set(key, load);
  }

  return load;
}

/**
 * A member that fails to load here loads again when it renders, so the
 * failure does not fail the module.
 */
async function preloadMembers(sdkMembers: readonly string[] | null): Promise<void> {
  if (sdkMembers === null) {
    return;
  }

  try {
    await preloadSdkMembers(sdkMembers);
  } catch {
    // The member's own render reports a load that fails again.
  }
}

async function importModule(
  specifier: string,
  features: readonly string[],
  sdkMembers: readonly string[] | null,
  importer: ModuleImporter
): Promise<LoadedWorkspaceModule> {
  installPageHost();
  const members = preloadMembers(sdkMembers);

  try {
    // A module can read `Sdk.Dnd` at its top level, so Dnd loads before it.
    if (features.includes('dnd')) {
      await preloadSdkDnd();
    }

    const [module] = await Promise.all([importer(specifier), members]);

    if (typeof module.default !== 'function') {
      return { Component: null, error: new Error(`${specifier} has no default export component.`) };
    }

    return {
      Component: module.default as React.FunctionComponent<Record<string, unknown>>,
      error: null
    };
  } catch (error) {
    return {
      Component: null,
      error: error instanceof Error ? error : new Error(`${specifier} could not load.`)
    };
  }
}
